// This is the component for the sampling events tables, will contain the the active sampling events and the completed sampling events

import styles from "./SamplingEventsTable.module.css";
import { DeleteSamplingEventButton } from "../buttons/SamplingEventActions";

// Active Sampling Events Table
export const ActiveSamplingEventsTable = ({
  activeSamplingEvents,
  onRowSelect,
  onDoubleClick,
  onDelete,
  selectedActiveSamplingEventId,
  loading,
  error,
  pagination,
  onPageChange,
}) => {
  // Show loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading sampling events...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
        <button
          className={styles.retryButton}
          onClick={() => onPageChange(pagination.currentPage)}
        >
          Retry
        </button>
      </div>
    );
  }

  // Show empty state
  if (!activeSamplingEvents || activeSamplingEvents.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <p>No active sampling events found.</p>
      </div>
    );
  }

  // Helper function for displaying event dates
  const formatEventDates = (startDate, endDate) => {
    if (startDate && endDate) {
      return `${startDate} to ${endDate}`;
    } else if (startDate) {
      return startDate;
    } else if (endDate) {
      return endDate;
    }
    return "";
  };

  return (
    <div className={styles.delTableContainer} id="delDraftTable">
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {activeSamplingEvents.map((activeSamplingEvent) => (
            <div
              key={`card-${activeSamplingEvent.id}`}
              onClick={() => onRowSelect(activeSamplingEvent)}
              onDoubleClick={() => onDoubleClick(activeSamplingEvent.id)}
              className={`${styles.card} ${
                activeSamplingEvent.id === selectedActiveSamplingEventId
                  ? styles.selectedCard
                  : ""
              }`}
            >
              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Created Date:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.created_at}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Updated Date:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.updated_at}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Event Dates:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.start_date}
                    {activeSamplingEvent.start_date &&
                      activeSamplingEvent.end_date &&
                      " to "}
                    {activeSamplingEvent.end_date}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Sampling Event Name:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.event_name}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Site Location:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.well_site || "-"}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Number:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.project_number || "-"}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Phase ID:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.phase_id}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Task ID:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.task_id}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Team Lead:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.team_lead_name}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Created By:</span>
                  <span className={styles.value}>
                    {activeSamplingEvent.created_by_name || "-"}
                  </span>
                </div>
              </div>

              <div className={styles.cardActions}>
                <DeleteSamplingEventButton
                  deleteSamplingEventFunction={() =>
                    onDelete(activeSamplingEvent.id)
                  }
                  className={styles.deleteButton}
                >
                  Delete
                </DeleteSamplingEventButton>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.maxWidthCol}>Created Date</th>
            <th className={styles.maxWidthCol}>Updated Date</th>
            <th className={styles.maxWidthCol}>Event Dates</th>
            <th className={styles.eventNameCol}>Event Name</th>
            <th className={styles.siteLocationCol}>Site Location</th>
            <th className={styles.maxWidthCol}>Project Number</th>
            <th className={styles.maxWidthCol}>Phase ID</th>
            <th className={styles.maxWidthCol}>Task ID</th>
            <th className={styles.teamLeadCol}>Team Lead</th>
            <th className={styles.createdByCol}>Created By</th>
            <th className={styles.deleteColumn}>Delete</th>
          </tr>
        </thead>
        <tbody>
          {activeSamplingEvents.map((activeSamplingEvent) => (
            <tr
              key={`row-${activeSamplingEvent.id}`}
              onClick={() => onRowSelect(activeSamplingEvent)}
              onDoubleClick={() => onDoubleClick(activeSamplingEvent.id)}
              className={
                activeSamplingEvent.id === selectedActiveSamplingEventId
                  ? styles.selectedRow
                  : ""
              }
            >
              <td title={activeSamplingEvent.created_at}>
                {activeSamplingEvent.created_at}
              </td>
              <td title={activeSamplingEvent.updated_at}>
                {activeSamplingEvent.updated_at}
              </td>
              <td
                title={formatEventDates(
                  activeSamplingEvent.start_date,
                  activeSamplingEvent.end_date
                )}
              >
                {formatEventDates(
                  activeSamplingEvent.start_date,
                  activeSamplingEvent.end_date
                )}
              </td>
              <td
                title={activeSamplingEvent.event_name}
                className={styles.eventNameCol}
              >
                {activeSamplingEvent.event_name}
              </td>
              <td
                title={activeSamplingEvent.well_site || "-"}
                className={styles.siteLocationCol}
              >
                {activeSamplingEvent.well_site || "-"}
              </td>
              <td title={activeSamplingEvent.project_number || "-"}>
                {activeSamplingEvent.project_number || "-"}
              </td>
              <td title={activeSamplingEvent.phase_id}>
                {activeSamplingEvent.phase_id}
              </td>
              <td title={activeSamplingEvent.task_id}>
                {activeSamplingEvent.task_id}
              </td>
              <td
                title={activeSamplingEvent.team_lead_name}
                className={styles.teamLeadCol}
              >
                {activeSamplingEvent.team_lead_name}
              </td>
              <td
                title={activeSamplingEvent.created_by_name || "-"}
                className={styles.createdByCol}
              >
                {activeSamplingEvent.created_by_name || "-"}
              </td>
              <td className={styles.deleteColumn}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(activeSamplingEvent.id);
                  }}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Completed Sampling Events Table
export const CompletedSamplingEventsTable = ({
  completedSamplingEvents,
  onRowSelect,
  onDoubleClick,
  selectedCompletedSamplingEventId,
  loading,
  error,
  pagination,
  onPageChange,
}) => {
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading sampling events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
        <button
          className={styles.retryButton}
          onClick={() => onPageChange(pagination.currentPage)}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!completedSamplingEvents || completedSamplingEvents.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <p>No completed sampling events found.</p>
      </div>
    );
  }

  const formatEventDates = (startDate, endDate) => {
    if (startDate && endDate) return `${startDate} to ${endDate}`;
    if (startDate) return startDate;
    if (endDate) return endDate;
    return "";
  };

  return (
    <div className={styles.delTableContainer} id="delCompletedTable">
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {completedSamplingEvents.map((evt) => (
            <div
              key={`card-${evt.id}`}
              onClick={() => onRowSelect(evt)}
              onDoubleClick={() => onDoubleClick(evt.id)}
              className={`${styles.card} ${
                evt.id === selectedCompletedSamplingEventId
                  ? styles.selectedCard
                  : ""
              }`}
            >
              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Completed Date:</span>
                  <span className={styles.value}>
                    {evt.completed_date || "-"}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Completed Time:</span>
                  <span className={styles.value}>
                    {evt.completed_time || "-"}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Completed By:</span>
                  <span className={styles.value}>
                    {evt.completed_by_name || "-"}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Event Dates:</span>
                  <span className={styles.value}>
                    {evt.start_date}
                    {evt.start_date && evt.end_date && " to "}
                    {evt.end_date}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Sampling Event Name:</span>
                  <span className={styles.value}>{evt.event_name}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Site Location:</span>
                  <span className={styles.value}>{evt.well_site || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Number:</span>
                  <span className={styles.value}>
                    {evt.project_number || "-"}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Phase ID:</span>
                  <span className={styles.value}>{evt.phase_id}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Task ID:</span>
                  <span className={styles.value}>{evt.task_id}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Team Lead:</span>
                  <span className={styles.value}>{evt.team_lead_name}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Created By:</span>
                  <span className={styles.value}>
                    {evt.created_by_name || "-"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.maxWidthCol}>Completed Date</th>
            <th className={styles.maxWidthCol}>Completed Time</th>
            <th className={styles.maxWidthCol}>Completed By</th>
            <th className={styles.maxWidthCol}>Event Dates</th>
            <th className={styles.eventNameCol}>Event Name</th>
            <th className={styles.siteLocationCol}>Site Location</th>
            <th className={styles.maxWidthCol}>Project Number</th>
            <th className={styles.maxWidthCol}>Phase ID</th>
            <th className={styles.maxWidthCol}>Task ID</th>
            <th className={styles.teamLeadCol}>Team Lead</th>
            <th className={styles.createdByCol}>Created By</th>
          </tr>
        </thead>
        <tbody>
          {completedSamplingEvents.map((evt) => (
            <tr
              key={`row-${evt.id}`}
              onClick={() => onRowSelect(evt)}
              onDoubleClick={() => onDoubleClick(evt.id)}
              className={
                evt.id === selectedCompletedSamplingEventId
                  ? styles.selectedRow
                  : ""
              }
            >
              <td title={evt.completed_date}>{evt.completed_date || "-"}</td>
              <td title={evt.completed_time}>{evt.completed_time || "-"}</td>
              <td title={evt.completed_by_name || "-"}>
                {evt.completed_by_name || "-"}
              </td>
              <td title={formatEventDates(evt.start_date, evt.end_date)}>
                {formatEventDates(evt.start_date, evt.end_date)}
              </td>
              <td title={evt.event_name} className={styles.eventNameCol}>
                {evt.event_name}
              </td>
              <td
                title={evt.well_site || "-"}
                className={styles.siteLocationCol}
              >
                {evt.well_site || "-"}
              </td>
              <td title={evt.project_number || "-"}>
                {evt.project_number || "-"}
              </td>
              <td title={evt.phase_id}>{evt.phase_id}</td>
              <td title={evt.task_id}>{evt.task_id}</td>
              <td title={evt.team_lead_name} className={styles.teamLeadCol}>
                {evt.team_lead_name}
              </td>
              <td
                title={evt.created_by_name || "-"}
                className={styles.createdByCol}
              >
                {evt.created_by_name || "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
